import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import { CustomWageFields } from "../../../components/CustomWageFields";
import {
  CUSTOM_WAGE_AMOUNT_ERROR,
  DEFAULT_CUSTOM_WAGE_FREQUENCY,
  isValidCustomWageAmount,
} from "../../../utils/custom-wage";
import {
  CUSTOM_WAGE_CHOICE_KEY,
  buildStandardWageChoices,
  getInitialWageChoiceKey,
} from "../../../utils/edit-wage-choices";
import type { OrgPendingTimeEntry } from "../../hooks/usePendingTimeEntries";

const CONFLICT_ERROR_CODE = "CONFLICT";

const CUSTOM_WAGE_LABEL = "Custom wage";
const WAGE_UPDATED_MESSAGE = "Wage updated";
const WAGE_UPDATE_FAILED_MESSAGE = "Failed to update wage";
const EDIT_WAGE_DESCRIPTION =
  "A custom wage applies to the whole shift. Choosing a standard wage removes it.";
const PROVIDER_NOTES_NOTE =
  "Provider notes about pay are not applied automatically.";
const NO_STANDARD_WAGES_MESSAGE =
  "This shift has no standard wage to choose, so set a custom wage.";

interface EditCustomWageButtonProps {
  entry: OrgPendingTimeEntry;
  organizationBusinessUnitId: string;
  onSaved: () => void;
}

export function EditCustomWageButton({
  entry,
  organizationBusinessUnitId,
  onSaved,
}: EditCustomWageButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-slate-600"
        onClick={() => setOpen(true)}
      >
        <Pencil className="mr-1 h-3 w-3" />
        Edit wage
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <EditCustomWageForm
            entry={entry}
            organizationBusinessUnitId={organizationBusinessUnitId}
            onSaved={onSaved}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

interface EditCustomWageFormProps extends EditCustomWageButtonProps {
  onClose: () => void;
}

// Rendered inside DialogContent so its state resets each time the dialog opens.
function EditCustomWageForm({
  entry,
  organizationBusinessUnitId,
  onSaved,
  onClose,
}: EditCustomWageFormProps) {
  const trpc = useTRPC();
  const choices = buildStandardWageChoices(entry.standardWageOptions);
  const hasStandardChoices = choices.length > 0;
  const initialChoiceKey = getInitialWageChoiceKey({ entry, choices });
  // With no standard wages, custom is the only choice.
  const [choiceKey, setChoiceKey] = useState(
    hasStandardChoices ? initialChoiceKey : CUSTOM_WAGE_CHOICE_KEY,
  );
  const [amount, setAmount] = useState(entry.customWage?.amount ?? "");
  const [frequency, setFrequency] = useState(
    entry.customWage?.wageFrequencyId ?? DEFAULT_CUSTOM_WAGE_FREQUENCY,
  );
  const [amountError, setAmountError] = useState<string>();

  const mutationCallbacks = {
    onSuccess: () => {
      toast.success(WAGE_UPDATED_MESSAGE);
      onSaved();
      onClose();
    },
    onError: (error: { message: string; data?: { code?: string } | null }) => {
      toast.error(error.message || WAGE_UPDATE_FAILED_MESSAGE);
      // The reviewer approved against a wage that has since changed, so show
      // them the current one.
      if (error.data?.code === CONFLICT_ERROR_CODE) {
        onSaved();
        onClose();
      }
    },
  };

  const customMutation = useMutation(
    trpc.timeEntry.updateShiftCustomWage.mutationOptions(mutationCallbacks),
  );
  const standardMutation = useMutation(
    trpc.timeEntry.updateTimeEntryStandardWage.mutationOptions(
      mutationCallbacks,
    ),
  );
  const isPending = customMutation.isPending || standardMutation.isPending;

  const isCustomSelected = choiceKey === CUSTOM_WAGE_CHOICE_KEY;
  const selectedChoice = choices.find((choice) => choice.key === choiceKey);

  let hasChanged = false;
  if (isCustomSelected) {
    hasChanged =
      amount.trim() !== entry.customWage?.amount ||
      frequency !== entry.customWage?.wageFrequencyId;
  } else if (selectedChoice) {
    hasChanged = choiceKey !== initialChoiceKey;
  }

  const handleSave = () => {
    if (isCustomSelected) {
      if (!isValidCustomWageAmount(amount)) {
        setAmountError(CUSTOM_WAGE_AMOUNT_ERROR);
        return;
      }
      customMutation.mutate({
        organizationBusinessUnitId,
        shiftId: entry.shiftId,
        customWage: { amount: amount.trim(), wageFrequencyId: frequency },
        expectedCustomWage: entry.customWage,
      });
      return;
    }
    if (!selectedChoice) {
      return;
    }
    standardMutation.mutate({
      organizationBusinessUnitId,
      timeEntryId: entry.timeEntryId,
      baseWageType: selectedChoice.option.baseWageType,
      isOvertime: selectedChoice.option.isOvertime,
      expected: {
        baseWageType: entry.baseWageType,
        isOvertime: entry.isOvertime,
        customWage: entry.customWage,
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit wage</DialogTitle>
        <DialogDescription>
          {EDIT_WAGE_DESCRIPTION} {PROVIDER_NOTES_NOTE}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {!hasStandardChoices && (
          <p className="text-sm text-slate-500">{NO_STANDARD_WAGES_MESSAGE}</p>
        )}
        <RadioGroup
          value={choiceKey ?? ""}
          onValueChange={setChoiceKey}
          disabled={isPending}
          aria-label="Wage"
        >
          {choices.map((choice) => {
            const optionId = `edit-wage-${choice.key}`;
            return (
              <WageChoiceCard
                key={choice.key}
                id={optionId}
                value={choice.key}
                isSelected={choice.key === choiceKey}
              >
                {choice.label}
              </WageChoiceCard>
            );
          })}
          <WageChoiceCard
            id="edit-wage-custom"
            value={CUSTOM_WAGE_CHOICE_KEY}
            isSelected={isCustomSelected}
          >
            {CUSTOM_WAGE_LABEL}
          </WageChoiceCard>
        </RadioGroup>

        {isCustomSelected && (
          <CustomWageFields
            idPrefix="edit-custom-wage"
            amount={amount}
            frequency={frequency}
            onAmountChange={(value) => {
              setAmount(value);
              setAmountError(undefined);
            }}
            onFrequencyChange={setFrequency}
            error={amountError}
            disabled={isPending}
          />
        )}
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isPending || !hasChanged}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save wage
        </Button>
      </DialogFooter>
    </>
  );
}

function WageChoiceCard({
  id,
  value,
  isSelected,
  children,
}: {
  id: string;
  value: string;
  isSelected: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border p-3",
        isSelected && "border-slate-400 bg-slate-50",
      )}
    >
      <RadioGroupItem id={id} value={value} className="mt-0.5" />
      <Label
        htmlFor={id}
        className="min-w-0 cursor-pointer text-sm font-normal text-slate-700"
      >
        {children}
      </Label>
    </div>
  );
}
